/* ==========================================================================
   PremiumUK — global.js
   Vanilla JS. No dependencies. Handles: drawers, sticky header, cart,
   predictive search, accordions, product gallery, variants, sticky ATC.
   ========================================================================== */
(function () {
  'use strict';

  const money = (cents) => {
    const format = window.PremiumUK && window.PremiumUK.moneyFormat;
    const amount = (cents / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (format) return format.replace(/\{\{\s*amount\s*\}\}/, amount);
    return '£' + amount;
  };

  const routes = (window.PremiumUK && window.PremiumUK.routes) || {};

  /* ---- Toast ------------------------------------------------------------- */
  let toastEl;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-visible');
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(() => toastEl.classList.remove('is-visible'), 2600);
  }

  /* ---- Drawer controller ------------------------------------------------- */
  const Drawers = {
    overlay: null,
    active: null,
    init() {
      this.overlay = document.querySelector('[data-drawer-overlay]');
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-drawer-open]');
        if (opener) { e.preventDefault(); this.open(opener.getAttribute('data-drawer-open')); }
        const closer = e.target.closest('[data-drawer-close]');
        if (closer) { e.preventDefault(); this.close(); }
      });
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    },
    open(id) {
      const el = document.getElementById(id);
      if (!el) return;
      this.close();
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-open');
      document.body.classList.add('no-scroll');
      this.active = el;
      const focusable = el.querySelector('input, button, a, [tabindex]');
      if (focusable) setTimeout(() => focusable.focus(), 120);
    },
    close() {
      if (this.active) { this.active.classList.remove('is-open'); this.active.setAttribute('aria-hidden', 'true'); this.active = null; }
      document.querySelectorAll('.drawer.is-open, .search-modal.is-open').forEach((d) => d.classList.remove('is-open'));
      if (this.overlay) this.overlay.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    }
  };

  /* ---- Sticky header ----------------------------------------------------- */
  function initStickyHeader() {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-stuck', y > 8);
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- Predictive search ------------------------------------------------- */
  function initSearch() {
    const modal = document.getElementById('SearchModal');
    if (!modal) return;
    const input = modal.querySelector('[data-search-input]');
    const results = modal.querySelector('[data-search-results]');
    if (!input) return;
    let timer;
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-open]')) {
        e.preventDefault();
        modal.classList.add('is-open');
        if (Drawers.overlay) Drawers.overlay.classList.add('is-open');
        document.body.classList.add('no-scroll');
        setTimeout(() => input.focus(), 120);
      }
    });
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      timer = setTimeout(() => fetchPredictive(q, results), 250);
    });

    // Inline header search (desktop) — its own results dropdown
    document.querySelectorAll('[data-header-search]').forEach((form) => {
      const hi = form.querySelector('[data-search-input]');
      const hr = form.querySelector('[data-search-results]');
      if (!hi || !hr) return;
      let t2;
      hi.addEventListener('input', () => {
        clearTimeout(t2);
        const q = hi.value.trim();
        if (q.length < 2) { hr.innerHTML = ''; return; }
        t2 = setTimeout(() => fetchPredictive(q, hr), 250);
      });
      document.addEventListener('click', (e) => { if (!form.contains(e.target)) hr.innerHTML = ''; });
    });
  }

  function fetchPredictive(q, container) {
    const url = (routes.predictive_search_url || '/search/suggest') +
      '?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=6&section_id=predictive-search';
    fetch(url)
      .then((r) => r.text())
      .then((text) => {
        try {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const inner = doc.querySelector('[data-predictive-results]');
          container.innerHTML = inner ? inner.innerHTML : '';
        } catch (e) { container.innerHTML = ''; }
      })
      .catch(() => { container.innerHTML = ''; });
  }

  /* ---- Cart -------------------------------------------------------------- */
  const Cart = {
    init() {
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-product-form]');
        if (form) { e.preventDefault(); this.addFromForm(form); }
      });
      document.addEventListener('click', (e) => {
        const dec = e.target.closest('[data-qty-decrease]');
        const inc = e.target.closest('[data-qty-increase]');
        if (dec || inc) {
          const wrap = (dec || inc).closest('.qty-selector');
          const field = wrap.querySelector('input');
          let val = parseInt(field.value, 10) || 1;
          val = inc ? val + 1 : Math.max(parseInt(field.min || '1', 10), val - 1);
          field.value = val;
          field.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.changeLine(remove.getAttribute('data-cart-remove'), 0); }
        const quick = e.target.closest('[data-quick-add]');
        if (quick) { e.preventDefault(); this.addById(quick.getAttribute('data-quick-add'), quick); }
      });
      document.addEventListener('change', (e) => {
        const line = e.target.closest('[data-cart-qty]');
        if (line) this.changeLine(line.getAttribute('data-cart-qty'), parseInt(line.value, 10));
      });
    },
    addById(id, btn) {
      if (btn) btn.setAttribute('aria-disabled', 'true');
      fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify({ id: id, quantity: 1 })
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => { if (!ok) { toast(data.description || 'Could not add to basket.'); return; } this.refresh(true); })
        .catch(() => toast('Could not add to basket. Please try again.'))
        .finally(() => { if (btn) btn.removeAttribute('aria-disabled'); });
    },
    addFromForm(form) {
      const btn = form.querySelector('[type="submit"]');
      const original = btn ? btn.innerHTML : '';
      if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.innerHTML = 'Adding…'; }
      const formData = new FormData(form);
      fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript' },
        body: formData
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) { toast(data.description || 'Could not add to basket.'); return; }
          this.refresh(true);
        })
        .catch(() => toast('Could not add to basket. Please try again.'))
        .finally(() => { if (btn) { btn.removeAttribute('aria-disabled'); btn.innerHTML = original; } });
    },
    changeLine(key, qty) {
      fetch(routes.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then((r) => r.json())
        .then(() => this.refresh(false))
        .catch(() => toast('There was a problem updating your basket.'));
    },
    refresh(openDrawer) {
      fetch((routes.cart_url || '/cart') + '?section_id=cart-drawer')
        .then((r) => r.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const newInner = doc.querySelector('[data-cart-drawer-inner]');
          const current = document.querySelector('[data-cart-drawer-inner]');
          if (newInner && current) current.innerHTML = newInner.innerHTML;
          this.updateBubble();
          if (openDrawer && (window.PremiumUK.cartType === 'drawer')) {
            Drawers.open('CartDrawer');
          } else if (openDrawer) {
            window.location.href = routes.cart_url || '/cart';
          }
        });
    },
    updateBubble() {
      fetch(routes.cart_url ? routes.cart_url + '.js' : '/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          document.querySelectorAll('[data-cart-count]').forEach((el) => {
            el.textContent = cart.item_count;
            el.hidden = cart.item_count === 0;
          });
        });
    }
  };

  /* ---- FAQ / accordions (native <details> — progressive) ----------------- */
  // Native <details> handles most; this ensures single-open groups if desired.
  function initAccordionGroups() {
    document.querySelectorAll('[data-accordion-single]').forEach((group) => {
      group.addEventListener('toggle', (e) => {
        const t = e.target;
        if (t.tagName === 'DETAILS' && t.open) {
          group.querySelectorAll('details[open]').forEach((d) => { if (d !== t) d.open = false; });
        }
      }, true);
    });
  }

  /* ---- Product gallery --------------------------------------------------- */
  function initGallery() {
    const gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;
    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    let index = 0;
    const show = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
      thumbs.forEach((t, n) => { t.classList.toggle('is-active', n === index); if (n === index) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
    };
    gallery.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-gallery-thumb]');
      if (thumb) { show(thumbs.indexOf(thumb)); return; }
      if (e.target.closest('[data-gallery-next]')) show(index + 1);
      if (e.target.closest('[data-gallery-prev]')) show(index - 1);
    });
    // Show variant image when variant changes
    gallery.addEventListener('gallery:goto', (e) => {
      const id = e.detail;
      const target = slides.findIndex((s) => s.getAttribute('data-media-id') === String(id));
      if (target > -1) show(target);
    });
  }

  /* ---- Variant selection ------------------------------------------------- */
  function initVariants() {
    const root = document.querySelector('[data-product-root]');
    if (!root) return;
    const dataEl = document.querySelector('[data-variants-json]');
    if (!dataEl) return;
    let variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

    const optionInputs = Array.from(root.querySelectorAll('[data-option-input]'));
    const idInput = root.querySelector('[data-variant-id]');
    const priceEl = root.querySelector('[data-price-current]');
    const compareEl = root.querySelector('[data-price-compare]');
    const saveEl = root.querySelector('[data-price-save]');
    const atcBtn = root.querySelector('[data-atc]');
    const atcText = root.querySelector('[data-atc-text]');
    const soldNote = root.querySelector('[data-soldout-note]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const gallery = document.querySelector('[data-gallery]');

    function selectedOptions() {
      const groups = {};
      optionInputs.forEach((input) => {
        if (input.type === 'radio' && !input.checked) return;
        groups[input.getAttribute('data-option-position')] = input.value;
      });
      // For selects and pill-radios: read by position
      const positions = [...new Set(optionInputs.map((i) => i.getAttribute('data-option-position')))];
      return positions.map((p) => groups[p]);
    }

    function findVariant(opts) {
      return variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }

    function update() {
      const opts = selectedOptions();
      const variant = findVariant(opts);
      if (!variant) {
        if (atcBtn) { atcBtn.setAttribute('disabled', 'disabled'); }
        if (atcText) atcText.textContent = 'Unavailable';
        return;
      }
      if (idInput) idInput.value = variant.id;

      // Price
      if (priceEl) priceEl.textContent = money(variant.price);
      if (stickyPrice) stickyPrice.textContent = money(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = money(variant.compare_at_price);
          compareEl.hidden = false;
          if (saveEl) {
            const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
            saveEl.textContent = 'Save ' + pct + '%';
            saveEl.hidden = false;
          }
        } else {
          compareEl.hidden = true;
          if (saveEl) saveEl.hidden = true;
        }
      }

      // Availability — never show Add to Basket on a sold-out variant
      if (variant.available) {
        if (atcBtn) { atcBtn.removeAttribute('disabled'); atcBtn.style.display = ''; }
        if (atcText) atcText.textContent = window.PremiumUK.strings.addToCart;
        if (soldNote) soldNote.hidden = true;
      } else {
        if (atcBtn) { atcBtn.setAttribute('disabled', 'disabled'); }
        if (atcText) atcText.textContent = window.PremiumUK.strings.soldOut;
        if (soldNote) soldNote.hidden = false;
      }

      // Update selected-pill styling for availability
      updatePillAvailability(opts);

      // Gallery
      if (gallery && variant.featured_media_id) {
        gallery.dispatchEvent(new CustomEvent('gallery:goto', { detail: variant.featured_media_id }));
      }

      // URL (no reload)
      if (history.replaceState && variant.id) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }

    function updatePillAvailability(currentOpts) {
      root.querySelectorAll('[data-option-input]').forEach((input) => {
        const pos = parseInt(input.getAttribute('data-option-position'), 10) - 1;
        const testOpts = currentOpts.slice();
        testOpts[pos] = input.value;
        const match = variants.find((v) => v.options.every((o, i) => (i === pos ? o === input.value : o === testOpts[i])));
        const label = input.closest('.variant-pill');
        if (label) label.classList.toggle('is-unavailable', !(match && match.available));
      });
    }

    root.addEventListener('change', (e) => { if (e.target.closest('[data-option-input]')) update(); });
    update();
  }

  /* ---- Sticky mobile ATC ------------------------------------------------- */
  function initStickyATC() {
    const bar = document.querySelector('[data-sticky-atc]');
    const anchor = document.querySelector('[data-atc]');
    if (!bar || !anchor) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ---- Recently viewed --------------------------------------------------- */
  function trackRecentlyViewed() {
    const root = document.querySelector('[data-product-root]');
    if (!root) return;
    const handle = root.getAttribute('data-product-handle');
    if (!handle) return;
    try {
      let seen = JSON.parse(localStorage.getItem('ns_recently_viewed') || '[]');
      seen = seen.filter((h) => h !== handle);
      seen.unshift(handle);
      seen = seen.slice(0, 8);
      localStorage.setItem('ns_recently_viewed', JSON.stringify(seen));
    } catch (e) { /* storage unavailable — non-critical */ }
  }

  function renderRecentlyViewed() {
    const container = document.querySelector('[data-recently-viewed]');
    if (!container) return;
    let handles = [];
    try { handles = JSON.parse(localStorage.getItem('ns_recently_viewed') || '[]'); } catch (e) { return; }
    const current = container.getAttribute('data-current-handle');
    handles = handles.filter((h) => h !== current).slice(0, 4);
    if (!handles.length) { container.closest('[data-recently-viewed-section]')?.remove(); return; }
    Promise.all(handles.map((h) =>
      fetch('/products/' + h + '?section_id=card-fragment')
        .then((r) => (r.ok ? r.text() : ''))
        .then((t) => {
          if (!t) return '';
          const doc = new DOMParser().parseFromString(t, 'text/html');
          const card = doc.querySelector('[data-card-fragment]');
          return card ? '<li>' + card.outerHTML + '</li>' : '';
        })
        .catch(() => '')
    )).then((cards) => {
      const html = cards.filter(Boolean).join('');
      const section = container.closest('[data-recently-viewed-section]');
      if (!html) { if (section) section.remove(); return; }
      container.innerHTML = html;
      if (section) section.style.display = '';
    });
  }

  /* ---- Product recommendations (server-rendered fallback) ---------------- */
  function initRecommendations() {
    const el = document.querySelector('[data-recommendations]');
    if (!el) return;
    const url = el.getAttribute('data-url');
    if (!url) return;
    fetch(url)
      .then((r) => (r.ok ? r.text() : ''))
      .then((text) => {
        if (!text) { el.remove(); return; }
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.querySelector('.product-card')) {
          el.innerHTML = fresh.innerHTML;
        } else {
          el.remove();
        }
      })
      .catch(() => el.remove());
  }

  /* ---- Newsletter (progressive AJAX to keep on-page) --------------------- */
  // Shopify handles the form natively; nothing extra required. Placeholder hook.

  /* ---- Slideshow banner -------------------------------------------------- */
  function initSlideshow() {
    document.querySelectorAll('[data-slideshow]').forEach((root) => {
      const slides = Array.from(root.querySelectorAll('[data-slide]'));
      if (slides.length < 2) return;
      const counter = root.querySelector('[data-slide-current]');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const autoplay = root.getAttribute('data-autoplay') === 'true' && !reduce;
      const speed = parseInt(root.getAttribute('data-speed'), 10) || 6000;
      let index = 0, timer = null;

      function show(i) {
        index = (i + slides.length) % slides.length;
        slides.forEach((s, n) => {
          const active = n === index;
          s.classList.toggle('is-active', active);
          if (active) { s.removeAttribute('aria-hidden'); } else { s.setAttribute('aria-hidden', 'true'); }
        });
        if (counter) counter.textContent = index + 1;
      }
      function next() { show(index + 1); }
      function prev() { show(index - 1); }
      function start() { if (autoplay) { stop(); timer = setInterval(next, speed); } }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      root.addEventListener('click', (e) => {
        if (e.target.closest('[data-slide-next]')) { next(); start(); }
        if (e.target.closest('[data-slide-prev]')) { prev(); start(); }
      });
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);

      // Touch swipe
      let x0 = null;
      root.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      root.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) { dx < 0 ? next() : prev(); start(); }
        x0 = null;
      }, { passive: true });

      // Pause when off-screen
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((en) => { en.isIntersecting ? start() : stop(); });
        }, { threshold: 0.2 }).observe(root);
      } else { start(); }

      show(0);
      start();
    });
  }

  /* ---- Init -------------------------------------------------------------- */
  function init() {
    Drawers.init();
    Cart.init();
    initStickyHeader();
    initSearch();
    initAccordionGroups();
    initGallery();
    initVariants();
    initStickyATC();
    trackRecentlyViewed();
    renderRecentlyViewed();
    initRecommendations();
    initSlideshow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.PremiumUK = window.PremiumUK || {};
  window.PremiumUK.toast = toast;
  window.PremiumUK.Drawers = Drawers;
})();
